import { loginByUsername, logout } from '@/api/login'
import { getAuthUserInfo, getUserPrivilege } from '@/api/auth'
import { queryTenantMenus } from '@/api/menu'
import { getUserOfficeList } from '@/api/user'
import {
  getToken,
  setToken,
  setBranchId,
  setDeptId,
  setUserInfo,
  removeToken,
  getBranchId
} from '@/utils/auth'
import router, { resetRouter } from '@/router'
import { getUserRoles } from '@/api/workflow'
import { getObjectByCache } from '@/utils/basicDataCache'
import { basicCacheKeys } from '@/utils/cacheConfig'
import * as CommonUtil from '@/utils/commonUtil'
import LocalStorageUtils from '@/utils/localStorageUtils'
import { getLoading } from '@/utils/utilBaseElement'
import { getUserGroupsFunc } from '@/api/userGroup'

const state = {
  token: getToken(),
  userInfo: {},
  menuInfo: {},
  currentOffice: { officeId: 0, uuid: '' },
  currentDept: { departmentId: 0, uuid: '' },
  name: '',
  avatar: '',
  introduction: '',
  roles: [],
  roleIds: [],
  userGroups: [],
  config: {} // 用户相关配置数据
}

const mutations = {
  SET_CURRENT_OFFICE: (state, currentOffice) => {
    const officeId = Number(currentOffice.officeId)
    state.currentOffice = currentOffice
    setBranchId(officeId, state.userInfo.userId, state.userInfo.tenantId)
  },
  SET_CURRENT_DEPT: (state, currentDept) => {
    const deptId = Number(currentDept.departmentId) || 0
    state.currentDept = currentDept
    setDeptId(deptId, state.userInfo.userId, state.userInfo.tenantId)
  },
  SET_TOKEN: (state, token) => {
    state.token = token
  },
  SET_CONFIG_OPT_ENTRIES: (state, entries) => {
    state.config.entries = entries
  },
  SET_INTRODUCTION: (state, introduction) => {
    state.introduction = introduction
  },
  SET_NAME: (state, name) => {
    state.name = name
  },
  SET_USERINFO: (state, userInfo) => {
    state.userInfo = userInfo
  },
  SET_MENU_INFO: (state, menuInfo) => {
    state.menuInfo = menuInfo
  },
  SET_AVATAR: (state, avatar) => {
    state.avatar = avatar
  },
  SET_ROLES: (state, roles) => {
    state.roles = roles
  },
  SET_ROLE_IDS: (state, roles) => {
    state.roleIds = roles
  },
  SET_USER_GROUPS: (state, userGroups) => {
    state.userGroups = userGroups
  }
}

const actions = {
  setConfigOptEntries({ commit }, entries) { // 用户配置首页快捷入口
    commit('SET_CONFIG_OPT_ENTRIES', entries)
  },
  setCurrentOffice({ commit }, currentOffice) {
    commit('SET_CURRENT_OFFICE', currentOffice)
  },
  setCurrentDept({ commit }, currentDept) {
    commit('SET_CURRENT_DEPT', currentDept)
  },
  setRoles({ commit }, roles) {
    commit('SET_ROLES', roles)
  },
  // user login by sessionId
  login({ commit }, sessionId) {
    return new Promise((resolve, reject) => {
      commit('SET_TOKEN', sessionId)
      setToken(sessionId)
      resolve()
    })
  },
  // user login
  login2({ commit }, userInfo) {
    const { username, password } = userInfo
    return new Promise((resolve, reject) => {
      loginByUsername({ username: username.trim(), password: password }).then(response => {
        const data = response
        commit('SET_TOKEN', data.content)
        setToken(data.content)
        resolve()
      }).catch(error => {
        reject(error)
      })
    })
  },

  // get user info
  async getInfo({ commit, state }, cargoBranchId) {
    let branchId = Number(cargoBranchId)
    return new Promise((resolve, reject) => {
      // todo 此处校验用户信息(getAuthUserInfo)是否已经获取，防止重复获取
      // 先执行 getAuthUserInfo，然后用其结果调用 getUserPrivilege
      getAuthUserInfo(state.token).then(userInfoRes => {
        const userInfo = userInfoRes
        const data = userInfo.data
        const groupKey = LocalStorageUtils.getConfiguration('sessionGroupKey:' + data.userId)
        Promise.all([getUserPrivilege({ groupKey: groupKey }), getUserOfficeList(), getUserRoles(), getUserGroupsFunc({ userId: data.userId })]).then(async res => {
          const permissions = res[0]
          const currentUserOfficeList = res[1]
          const currentUserRoles = res[2].data
          const curUserGroups = res[3].data
          const currentOfficeList = currentUserOfficeList.data
          data.currentUserOfficeList = [...currentOfficeList]

          const menuInfo = await queryTenantMenus(data.tenantUuid).then(res => res.data)
          commit('SET_MENU_INFO', menuInfo)
          setUserInfo(data)
          commit('SET_USERINFO', data)
          commit('SET_ROLE_IDS', currentUserRoles)
          commit('SET_USER_GROUPS', curUserGroups)

          /* 校验cargoBranchId是否合法，并赋默认值 */
          let findCargoBranch = null
          if (branchId) {
            findCargoBranch = currentOfficeList.find(office => branchId === office.officeId)
          }
          if (!findCargoBranch) {
            const cacheBranchId = getBranchId(userInfo.data.userId, userInfo.data.tenantId)
            if (cacheBranchId) {
              branchId = Number(cacheBranchId)
              findCargoBranch = currentOfficeList.find(office => branchId === office.officeId)
            }
          }
          if (!findCargoBranch) {
            branchId = userInfo.data.defaultOfficeId
            findCargoBranch = currentOfficeList.find(office => branchId === office.officeId)
          }
          if (!findCargoBranch && !userInfo.data.groupCompany) {
            branchId = 2
            findCargoBranch = currentOfficeList.find(office => branchId === office.officeId)
          }
          const currentOffice = findCargoBranch || currentOfficeList[0]
          CommonUtil.getTimeZone(currentOffice) // 当前分公司所设置的时区
          commit('SET_CURRENT_OFFICE', currentOffice || {})
          CommonUtil.setBpmJobCharge() // 当前分公司是否开启“业务费用申请单”审批流

          // todo
          const roles = ['mdm:manage', 'mdm:seaport', ...permissions.data]
          if (roles.length > 0) { // 验证返回的roles是否是一个非空数组
            commit('SET_ROLES', roles)
          } else {
            reject('未向该用户分配任何角色！')
          }
          resolve({ roles })
        }).catch(error => {
          reject(error)
        })
      })
    })
  },

  // user logout
  logout({ commit, state }) {
    return new Promise((resolve, reject) => {
      logout(state.token).then(() => {
        commit('SET_TOKEN', '')
        commit('SET_ROLES', [])
        removeToken()
        // window.localStorage.clear()
        resetRouter()
        resolve()
      }).catch(error => {
        commit('SET_TOKEN', '')
        commit('SET_ROLES', [])
        removeToken()
        resetRouter()
        reject(error)
      })
    })
  },

  // remove token
  resetToken({ commit }) {
    return new Promise(resolve => {
      commit('SET_TOKEN', '')
      commit('SET_ROLES', [])
      removeToken()
      resolve()
    })
  },

  setUserInfo({ commit }, userInfo) {
    return new Promise(resolve => {
      commit('SET_USERINFO', userInfo)
      resolve()
    })
  },

  // Dynamically modify permissions
  changeRoles({ commit, dispatch }, role) {
    return new Promise(async resolve => {
      const token = role + '-token'

      commit('SET_TOKEN', token)
      setToken(token)

      const { roles } = await dispatch('getInfo')

      resetRouter()

      // generate accessible routes map based on roles
      const accessRoutes = await dispatch('permission/generateRoutes', roles, { root: true })

      // dynamically add accessible routes
      router.addRoutes(accessRoutes)

      resolve()
    })
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions
}
